import { Select } from "@sembark-travel/ui-base"
import { Fragment, useRef, useState, useMemo } from "react"
import { Omit, Optional } from "utility-types"

import { Dialog } from "@sembark-travel/ui-dialog"
import {
	ITransportService,
	NewServicesForm,
	TTransportServiceLocation,
} from "../TransportServices"
import { NewServiceCredentials } from "../TransportServices/store"
import { PERMISSIONS, useCheckPermissions } from "../Auth"

type SelectTransportServiceTypesProps = React.ComponentProps<typeof Select> & {
	serviceLocation?: TTransportServiceLocation
	onCreateSuccess?: (transportService: ITransportService) => void
}
type TTransportServiceOption = ITransportService & { label: string }
export function SelectTransportServicesTypes({
	serviceLocation,
	onCreateSuccess,
	...otherProps
}: SelectTransportServiceTypesProps) {
	const [newName, setNewName] = useState("")
	const { hasPermission } = useCheckPermissions()
	const { creatable } = otherProps
	const promiseOfCreation = useRef<
		((transportService?: TTransportServiceOption) => void) | undefined
	>(undefined)

	function settleCreation(value?: TTransportServiceOption) {
		promiseOfCreation.current?.(value)
		promiseOfCreation.current = undefined
	}
	const initialValues = useMemo(
		() => ({
			from: serviceLocation?.from as NewServiceCredentials["from"] | undefined,
			to: serviceLocation?.to as NewServiceCredentials["to"] | undefined,
			trip_destinations: serviceLocation?.trip_destinations,
			quick_add: true,
			use_same_locations: false,
			transport_service: [
				{
					service: newName,
					service_short_name: "",
				},
			],
		}),
		[
			newName,
			serviceLocation?.from,
			serviceLocation?.to,
			serviceLocation?.trip_destinations,
		]
	)
	const canCreateNewTransportServicetype =
		creatable && hasPermission(PERMISSIONS.CREATE_TRANSPORT_SERVICES)
	return (
		<Fragment>
			<Select
				{...otherProps}
				creatable={canCreateNewTransportServicetype}
				onCreateNew={(query: string) => {
					setNewName(query)

					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
			/>

			<EditTransportServiceDialog
				open={!!newName}
				onClose={() => {
					setNewName("")
					settleCreation()
				}}
				initialValues={initialValues}
				onSuccess={(transportService) => {
					const serviceType = {
						...transportService,
						label: transportService.service,
					}
					onCreateSuccess?.(transportService)
					settleCreation(serviceType)
					setNewName("")
				}}
				serviceLocationId={serviceLocation?.id}
				type="addLocationService"
				canAddMoreServices={false}
				readOnlyLocationFields={true}
				url={`/transport-services`}
			/>
		</Fragment>
	)
}

function EditTransportServiceDialog({
	open,
	onClose,
	onSuccess,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof NewServicesForm>, "onCancel">,
	"onSuccess"
> & {
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} lg>
			<Dialog.Header closeButton>
				<Dialog.Title>Add New Transport Service</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<NewServicesForm
					onSuccess={(...args) => {
						onSuccess?.(...args)
						onClose()
					}}
					onCancel={onClose}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
